/**
 * OrderStatusBadge — 訂單狀態標籤
 * Phase 2
 */

import {
  Archive,
  CheckCircle2,
  Clock,
  Package,
  PackageCheck,
  Truck,
  type LucideIcon,
} from "lucide-react";

import type { OrderStatusV2 } from "@/lib/types";

/**
 * chanli-ops pill 規格（L2，SKILL.md §9「狀態雙通道」）：
 * pill = icon + 中文標籤 + 色，三者缺一不可；tint 淺底＋暗色文字＋25% 語意色邊。
 * 六態命名統一：待處理／備貨中／待出貨／配送中／已送達／已封存。
 * 語意對映：待處理=warn／備貨中・待出貨・配送中=progress／已送達=done／
 * 已封存=中性（raised 底＋line 邊）。
 */
const STATUS_CONFIG: Record<
  OrderStatusV2,
  { label: string; className: string; Icon: LucideIcon }
> = {
  pending: {
    label: "待處理",
    className: "bg-warn-tint text-warn border-warn/25",
    Icon: Clock,
  },
  preparing: {
    label: "備貨中",
    className: "bg-progress-tint text-progress border-progress/25",
    Icon: Package,
  },
  ready_to_ship: {
    label: "待出貨",
    className: "bg-progress-tint text-progress border-progress/25",
    Icon: PackageCheck,
  },
  shipping: {
    label: "配送中",
    className: "bg-progress-tint text-progress border-progress/25",
    Icon: Truck,
  },
  delivered: {
    label: "已送達",
    className: "bg-done-tint text-done border-done/25",
    Icon: CheckCircle2,
  },
  archived: {
    label: "已封存",
    className: "bg-raised text-ink-muted border-line",
    Icon: Archive,
  },
};

interface OrderStatusBadgeProps {
  status: string;
  className?: string;
}

export default function OrderStatusBadge({ status, className = "" }: OrderStatusBadgeProps) {
  // Keep a compatibility fallback while historical rows are still being migrated.
  let normalized = status as OrderStatusV2;
  if (status === "temporary") normalized = "pending";
  if (status === "completed") normalized = "delivered";

  const config = STATUS_CONFIG[normalized] || STATUS_CONFIG.pending;
  const { Icon } = config;

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${config.className} ${className}`}
    >
      <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
      {config.label}
    </span>
  );
}
